import React, { useState } from 'react';
import './WorkSelector.css';
import Button from '../components/Button/Button';
import Modal from '../components/Modal/Modal';
import CardGallery from '../components/CardGallery/CardGallery';

// Import all images
import stoolPigeonLogo from '../components/CardGallery/images/stool-pigeon-logo.png';
import orchardLogo from '../images/orchard_logo.png';
import huskyFlashLogo from '../images/husky-flash-logo.png';
import aixFreewillLogo from '../images/aixfreewill_logo.png';
import personalLogo from '../images/personal-logo.png';
import cardback from '../components/CardGallery/images/cardback.png';
import bamboozle from '../components/CardGallery/images/bamboozle.png';
import kingpin from '../components/CardGallery/images/kingpin.png';
import meatball from '../components/CardGallery/images/meatball.png';
import rat from '../components/CardGallery/images/rat.png';
import stoolPigeon from '../components/CardGallery/images/stool_pigeon.png';
import vendetta from '../components/CardGallery/images/vendetta.png';
import witness2 from '../components/CardGallery/images/witness-2.png';
import witness3 from '../components/CardGallery/images/witness-3.png';
import witness4 from '../components/CardGallery/images/witness-4.png';
import witness5 from '../components/CardGallery/images/witness-5.png';
import witness6 from '../components/CardGallery/images/witness-6.png';
import witness7 from '../components/CardGallery/images/witness-7.png';
import witness8 from '../components/CardGallery/images/witness-8.png';
import witness9 from '../components/CardGallery/images/witness-9.png';
import witness10 from '../components/CardGallery/images/witness-10.png';
import addButton from '../components/CardGallery/images/add-button.png';
import doneButton from '../components/CardGallery/images/done-button.png';
import eliminateButton from '../components/CardGallery/images/eliminate-button.png';
import knockButton from '../components/CardGallery/images/knock-button.png';
import gameBackground from '../components/CardGallery/images/game-background.png';
import gameBackgroundLight from '../components/CardGallery/images/game-background-light.png';
import startButton from '../components/CardGallery/images/start-button.png';

// Define the groups
const cardGroups = [
  {
    name: 'Special Cards',
    cards: [
      { src: cardback, label: 'Card Back' },
      { src: bamboozle, label: 'Bamboozle' },
      { src: kingpin, label: 'Kingpin' },
      { src: meatball, label: 'Meatball' },
      { src: rat, label: 'Rat' },
      { src: stoolPigeon, label: 'Stool Pigeon' },
      { src: vendetta, label: 'Vendetta' },
    ]
  },
  {
    name: 'Witness Cards',
    cards: [
      { src: witness2, label: 'Witness 2' },
      { src: witness3, label: 'Witness 3' },
      { src: witness4, label: 'Witness 4' },
      { src: witness5, label: 'Witness 5' },
      { src: witness6, label: 'Witness 6' },
      { src: witness7, label: 'Witness 7' },
      { src: witness8, label: 'Witness 8' },
      { src: witness9, label: 'Witness 9' },
      { src: witness10, label: 'Witness 10' },
    ]
  }
];

const buttonGroups = [
  {
    name: 'Buttons',
    cards: [
      { src: startButton, label: 'Start Button' },
      { src: addButton, label: 'Add Button' },
      { src: doneButton, label: 'Done Button' },
      { src: eliminateButton, label: 'Eliminate Button' },
      { src: knockButton, label: 'Knock Button' },
    ]
  }
];

const backgroundGroups = [
  {
    name: 'Game Backgrounds',
    cards: [
      { src: gameBackground, label: 'Dark Theme' },
      { src: gameBackgroundLight, label: 'Light Theme' },
    ]
  }
];

function WorkSelector() {
  const [selected, setSelected] = useState('code');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showArchive, setShowArchive] = useState(false);

  // Example items — replace with real project data or props later
  const works = {
    code: [
      {
        id: 'c5',
        title: 'Orchard',
        desc: 'An open source terminal UI for visualizing Claude Code multi-agent sessions in real time.',
        tags: ['Go', 'TUI', 'Open Source', 'Claude Code'],
        wip: true,
        thumbnail: orchardLogo,
      },
      {
        id: 'c4',
        title: 'StoolPidgeon',
        desc: 'A full implementation of the card game Stool Pigeon and an AI research framework, with four agents ranging from rule-based heuristics to a Monte Carlo POMDP with Bayesian belief tracking.',
        tags: ['Python', 'PyGame', 'AI', 'Machine Learning'],
        thumbnail: stoolPigeonLogo,
      },
      { 
        id: 'c3',
        title: 'HuskyFlash',
        desc: 'iOS flashcard-based learning app designed for technical interview preparation.',
        tags: ['Swift', 'UIKit', 'Firebase', 'Figma'],
        thumbnail: huskyFlashLogo,
      },
      {
        id: 'c1',
        title: 'FakeStackOverflow',
        desc: 'Web app inspired by the popular StackOverFlow.',
        tags: ['React', 'TypeScript', 'Material UI', 'Node.js', 'Axios','MongoDB'],
        archived: true,
      },
      {
        id: 'c2',
        title: 'Kanbas',
        desc: 'Web app inspired by the popular LMS Canvas.',
        tags: ['React', 'Redux', 'Node.js', 'Axios', 'MongoDB'],
        archived: true,
      },
    ],
    design: [
      { 
        id: 'd1',
        title: 'AI x Freewill',
        desc: 'Interactive tools exploring AI influence on user choices.',
        tags: ['Figma', 'Illustrator', 'Adobe XD', 'User Research', 'Prototyping'],
        thumbnail: aixFreewillLogo,
      },
      { 
        id: 'd2',
        title: 'Personal Branding',
        desc: 'Logo and visual system for my personal brand.',
        tags: ['Figma', 'Graphic Design', 'Typography', 'Branding'],
        thumbnail: personalLogo,
      }
    ],
  };

  // Render different content based on project ID
  const renderProjectDetails = (project) => {
    if (!project) return null;

    switch (project.id) {
      case 'c5':
        return (
          <div>
            <p><strong>Description:</strong></p>
            <p>{project.desc}</p>

            <div className="project-links">
              {[
                { label: 'GitHub', href: 'https://github.com/hcarminati/orchard' },
              ].map(({ label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link-btn"
                >
                  {label} →
                </a>
              ))}
            </div>

            <p><strong>Technologies:</strong></p>
            <div className="tags-list">
              {project.tags.map((tag, index) => (
                <span key={`modal-tag-${index}`} className="tag">
                  {tag}
                </span>
              ))}
            </div>

            <p className="project-about"><strong>About the Project:</strong></p>
            <p>
              Orchard is a terminal UI built in Go that lets you watch Claude Code multi-agent sessions unfold in real time. It renders the live agent graph — tasks, subagents, tool calls, and message flow — directly in your terminal, giving you a clear picture of what each agent is doing and how they relate to one another.
            </p>
          </div>
        );
      case 'c4':
        return (
          <div>
            <p><strong>Description:</strong></p>
            <p>{project.desc}</p>

            <div className="project-links">
              {[
                { label: 'Play the Game', href: 'https://stool-pigeon.netlify.app' },
                { label: 'GitHub', href: 'https://github.com/hcarminati/Stool-Pidgeon' },
                { label: 'Final Paper', href: 'https://github.com/hcarminati/Stool-Pidgeon/blob/main/CS%205100%20Final%20Project_HeatherCarminati.pdf' },
              ].map(({ label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link-btn"
                >
                  {label} →
                </a>
              ))}
            </div>

            <p><strong>Technologies:</strong></p>
            <div className="tags-list">
              {project.tags.map((tag, index) => (
                <span key={`modal-tag-${index}`} className="tag">
                  {tag}
                </span>
              ))}
            </div>
            <p className="project-about"><strong>About the Game:</strong></p>
            <p>
              Stool Pigeon is a competitive memory-based card game where each player manages a "crime scene" of four face-down cards and tries to minimize their total score. Unlike poker, both players have hidden cards, so the AI must track uncertainty about its own unseen cards as well as the opponent's. Four special action cards (Stool Pigeon, Bamboozle, Vendetta, and Kingpin) each create distinct information events the agent must reason about. Players can also "knock" at any time to end the round, adding a timing dimension to strategy.
            </p>
            <p><strong>What I Built:</strong></p>
            <p>
              I built the full game engine from scratch in Python and PyGame, supporting both a playable GUI mode and a headless mode for automated agent evaluation. I then implemented four AI agents of increasing sophistication and ran a systematic evaluation across 500-game matchups to measure the contribution of each layer of reasoning.
            </p>
            <p>
              The <strong>random agent</strong> selects legal actions at random (baseline). The <strong>heuristic agent</strong> applies fixed rules based on card values, winning 56.6% against random. The <strong>POMDP agent</strong> maintains a belief state over all hidden cards and updates it using Bayesian inference as cards are revealed through peeks, discards, and swaps, winning 79.0% against random and 81.6% against the heuristic agent with a knock accuracy of 91.3%. The <strong>MC+POMDP agent</strong>, my most advanced agent, replaces fixed draw and knock heuristics with Monte Carlo simulation, running 100 rollouts per decision to estimate downstream action value, winning 93.6% against the heuristic agent and 95.4% against the basic POMDP agent with a knock accuracy of 96.7%.
            </p>

            <div className="game-designs-section">
              <p><strong>Game Designs:</strong></p>
              <CardGallery groups={cardGroups} />
            </div>

            <div className="project-gallery-section">
              <CardGallery groups={buttonGroups} variant="button" />
            </div>

            <div className="project-gallery-section">
              <CardGallery groups={backgroundGroups} variant="background" />
            </div>
          </div>
        );
      case 'd2': // Personal Branding
        return (
          <div>
            <p><strong>Description:</strong></p>
            <p>{project.desc}</p>
            
            <p><strong>Technologies:</strong></p>
            <div className="tags-list">
              {project.tags.map((tag, index) => (
                <span key={`modal-tag-${index}`} className="tag">
                  {tag}
                </span>
              ))}
            </div>

            <div className="project-preview">
              <p><strong>Design Preview:</strong></p>
              <iframe
                className="project-iframe"
                src="https://embed.figma.com/design/LIlljPDu6YOKnPIKzgBpbJ/Personal-Brand?node-id=156-2353&embed-host=share"
                allowFullScreen
                title={`${project.title} Figma Design`}
              />
            </div>
          </div>
        );

      case 'd1': // AI x Freewill
        return (
          <div>
            <p><strong>Description:</strong></p>
            <p>{project.desc}</p>
            
            <p><strong>Technologies:</strong></p>
            <div className="tags-list">
              {project.tags.map((tag, index) => (
                <span key={`modal-tag-${index}`} className="tag">
                  {tag}
                </span>
              ))}
            </div>

            <div className="project-preview">
              <p><strong>Case Study:</strong></p>
              <iframe
                className="project-iframe"
                src="https://embed.figma.com/design/kPtCAFyZa7lTVVBdhLUmMV/Design-Degree-Project?node-id=705-2278&embed-host=share"
                allowFullScreen
                title={`${project.title} Figma Design`}
              />
            </div>

            <div className="project-preview">
              <p><strong>Website Prototype:</strong></p>
              <iframe
                className="project-iframe"
                src="https://embed.figma.com/design/kPtCAFyZa7lTVVBdhLUmMV/Design-Degree-Project?node-id=330-1166&embed-host=share"
                allowFullScreen
                title={`${project.title} Figma Design`}
              />
            </div>
          </div>
        );

      default:
        return (
          <div>
            <p><strong>Description:</strong></p>
            <p>{project.desc}</p>
            
            <p><strong>Technologies:</strong></p>
            <div className="tags-list">
              {project.tags.map((tag, index) => (
                <span key={`modal-tag-${index}`} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="work-selector">
      <div className="work-buttons">
        <Button
          onClick={() => setSelected('code')}
          isActive={selected === 'code'}
          aria-pressed={selected === 'code'}
        >
          Code
        </Button>
        <Button
          onClick={() => setSelected('design')}
          isActive={selected === 'design'}
          aria-pressed={selected === 'design'}
        >
          Design
        </Button>
      </div>

      <div className="work-list" role="list">
        {works[selected].filter(item => !item.archived).map((item) => (
          <div
            className="work-item"
            key={item.id}
            role="listitem"
            onClick={() => setSelectedProject(item)}
          >
            <div className="work-item-header">
              {item.thumbnail && (
                <img src={item.thumbnail} alt={`${item.title} thumbnail`} className="work-item-thumbnail" />
              )}
              <div className="work-item-header-text">
                <h3>{item.title}</h3>
                {item.wip && <span className="work-item-badge">Work In Progress</span>}
              </div>
            </div>
            <p>{item.desc}</p>
            <div className="tags-list">
              {item.tags.map((tag, index) => (
                <span key={`${item.id}-tag-${index}`} className="tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {selected === 'code' && works.code.some(item => item.archived) && (
        <>
          <button className="archive-toggle" onClick={() => setShowArchive(v => !v)}>
            {showArchive ? 'Hide archive ↑' : 'View archive ↓'}
          </button>
          {showArchive && (
            <div className="work-list work-list-archive" role="list">
              {works.code.filter(item => item.archived).map((item) => (
                <div
                  className="work-item"
                  key={item.id}
                  role="listitem"
                  onClick={() => setSelectedProject(item)}
                >
                  <div className="work-item-header">
                    {item.thumbnail && (
                      <img src={item.thumbnail} alt={`${item.title} thumbnail`} className="work-item-thumbnail" />
                    )}
                    <div className="work-item-header-text">
                      <h3>{item.title}</h3>
                    </div>
                  </div>
                  <p>{item.desc}</p>
                  <div className="tags-list">
                    {item.tags.map((tag, index) => (
                      <span key={`${item.id}-tag-${index}`} className="tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={selectedProject !== null}
        onClose={() => setSelectedProject(null)}
        title={selectedProject?.title}
        badge={selectedProject?.wip ? 'Work in Progress' : null}
      >
        {renderProjectDetails(selectedProject)}
      </Modal>
    </div>
  );
}

export default WorkSelector;
